import { useState } from "react";
import { Link, useRoute } from "wouter";
import { ArrowLeft, Pencil, Users } from "lucide-react";
import { PageHeader } from "@/components/layout/PageHeader";
import { ClientEditDialog } from "@/components/clients/ClientEditDialog";
import { OrderStatusBadge } from "@/components/dashboard/OrderStatusBadge";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { EmptyState } from "@/components/ui/empty-state";
import { Pagination } from "@/components/ui/pagination";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  useClient,
  useClientAddresses,
  useClientCoupons,
  useClientOrders,
  useClientSpending,
  useClientSubscriptions,
} from "@/hooks/use-clients";
import { usePagination } from "@/hooks/use-pagination";
import { formatCurrency, formatDate, formatDateTime, formatNumber, formatPhone } from "@/lib/format";
import { orderCode } from "@/lib/orderCode";

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <p className="text-caption font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-0.5 break-words text-small">{value || "-"}</p>
    </div>
  );
}

export default function ClientDetail() {
  const [, params] = useRoute("/clientes/:id");
  const id = params?.id;

  const client = useClient(id);
  const addresses = useClientAddresses(id);
  const orders = useClientOrders(id);
  const spending = useClientSpending(id);
  const coupons = useClientCoupons(id);
  const subscriptions = useClientSubscriptions(id);
  const addressesPage = usePagination(addresses.data ?? []);
  const couponsPage = usePagination(coupons.data ?? []);
  const subscriptionsPage = usePagination(subscriptions.data ?? []);
  const [editing, setEditing] = useState(false);

  if (client.isLoading) {
    return (
      <>
        <Skeleton className="mb-6 h-10 w-64" />
        <Skeleton className="h-64 w-full" />
      </>
    );
  }

  if (!client.data) {
    return (
      <EmptyState
        icon={Users}
        title="Cliente não encontrado"
        description="A conta pode ter sido excluída, ou o endereço está errado."
        action={
          <Button asChild variant="outline">
            <Link href="/clientes">Voltar para a lista</Link>
          </Button>
        }
      />
    );
  }

  const c = client.data;
  const deliveredCount = spending.data?.count ?? 0;
  const spent = spending.data?.sum ?? 0;

  return (
    <>
      <Button asChild variant="ghost" size="sm" className="mb-3 -ml-2">
        <Link href="/clientes">
          <ArrowLeft /> Clientes
        </Link>
      </Button>

      <PageHeader
        title={c.name || "Sem nome"}
        description={c.email || "Sem e-mail cadastrado."}
        actions={
          <Button variant="outline" onClick={() => setEditing(true)}>
            <Pencil /> Corrigir cadastro
          </Button>
        }
      />

      {c.userDeleted ? (
        <Card className="mb-4 border-border bg-muted/50">
          <CardContent className="p-4">
            <p className="text-small font-bold">Conta excluída pelo próprio cliente</p>
            <p className="mt-1 text-small text-muted-foreground">
              Os dados pessoais foram removidos e os pedidos foram anonimizados. O histórico
              continua, porque o faturamento dos estabelecimentos depende dele.
            </p>
          </CardContent>
        </Card>
      ) : null}

      <div className="mb-4 grid grid-cols-1 gap-3 xs:grid-cols-3">
        <Card>
          <CardContent className="p-4">
            <p className="text-caption uppercase tracking-wide text-muted-foreground">Pedidos entregues</p>
            <p className="text-h5 font-black">{formatNumber(deliveredCount)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-caption uppercase tracking-wide text-muted-foreground">Total gasto</p>
            <p className="text-h5 font-black">{formatCurrency(spent)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-caption uppercase tracking-wide text-muted-foreground">Favoritos</p>
            <p className="text-h5 font-black">{formatNumber(c.favoriteSellerIds?.length ?? 0)}</p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="cadastro">
        <TabsList>
          <TabsTrigger value="cadastro">Cadastro</TabsTrigger>
          <TabsTrigger value="pedidos">Pedidos</TabsTrigger>
          <TabsTrigger value="enderecos">Endereços</TabsTrigger>
          <TabsTrigger value="cupons">Cupons</TabsTrigger>
          <TabsTrigger value="assinaturas">Assinaturas</TabsTrigger>
        </TabsList>

        <TabsContent value="cadastro">
          <Card>
            <CardHeader>
              <CardTitle>Dados do cliente</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <Field label="E-mail" value={c.email} />
              <Field label="Telefone" value={formatPhone(c.phone)} />
              <Field label="CPF" value={c.cpf} />
              <Field label="Cadastrado em" value={formatDate(c.createdAt)} />
              <Field label="Atualizado em" value={formatDate(c.updatedAt)} />
              <Field label="Aparelhos com push" value={formatNumber(c.pushTokens?.length ?? 0)} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="pedidos">
          <Card>
            <CardHeader>
              <CardTitle>Pedidos</CardTitle>
              <CardDescription>Do mais recente para o mais antigo.</CardDescription>
            </CardHeader>
            <CardContent className="p-0 sm:p-0">
              {orders.isLoading ? (
                <div className="space-y-2 p-4">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Skeleton key={i} className="h-10 w-full" />
                  ))}
                </div>
              ) : orders.rows.length === 0 ? (
                <p className="p-4 text-small text-muted-foreground">Nenhum pedido registrado.</p>
              ) : (
                <Table className="table-stacked">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Pedido</TableHead>
                      <TableHead className="hidden md:table-cell">Estabelecimento</TableHead>
                      <TableHead className="hidden lg:table-cell">Data</TableHead>
                      <TableHead>Total</TableHead>
                      <TableHead>Situação</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {orders.rows.map((order) => (
                      <TableRow key={order.id}>
                        <TableCell className="cell-title font-mono text-caption">
                          {orderCode(order.id)}
                        </TableCell>
                        <TableCell className="hidden md:table-cell">
                          {order.sellerId ? (
                            <Link
                              href={`/vendedores/${order.sellerId}`}
                              className="hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                            >
                              {order.sellerName || "Estabelecimento"}
                            </Link>
                          ) : (
                            order.sellerName || "-"
                          )}
                        </TableCell>
                        <TableCell className="hidden lg:table-cell">
                          {formatDateTime(order.createdAt)}
                        </TableCell>
                        <TableCell className="tabular-nums font-semibold">
                          {formatCurrency(order.total)}
                        </TableCell>
                        <TableCell>
                          <OrderStatusBadge status={order.status} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              <Pagination {...orders.paginationProps} itemLabel="pedidos" />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="enderecos">
          <Card>
            <CardHeader>
              <CardTitle>Endereços</CardTitle>
              <CardDescription>
                Da subcoleção do cliente. Há também uma cópia no documento dele, herdada de uma
                versão anterior do aplicativo.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {addresses.isLoading ? (
                <Skeleton className="h-24 w-full" />
              ) : (addresses.data ?? []).length === 0 ? (
                <p className="py-4 text-small text-muted-foreground">Nenhum endereço cadastrado.</p>
              ) : (
                <ul className="space-y-3">
                  {addressesPage.pageItems.map((addr) => (
                    <li key={addr.id} className="rounded-md border border-border p-3">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-small font-bold">{addr.nickname || "Endereço"}</span>
                        {addr.isDefault ? <Badge variant="default">Padrão</Badge> : null}
                      </div>
                      <p className="mt-1 break-words text-small text-muted-foreground">
                        {[addr.street, addr.number, addr.complement].filter(Boolean).join(", ")}
                        {addr.neighborhood ? ` - ${addr.neighborhood}` : ""}
                        {addr.city ? `, ${addr.city}` : ""}
                        {addr.state ? `, ${addr.state}` : ""}
                        {addr.zipCode ? ` - ${addr.zipCode}` : ""}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
              <Pagination {...addressesPage.paginationProps} itemLabel="endereços" className="-mx-4 mt-3 sm:-mx-5" />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="cupons">
          <Card>
            <CardHeader>
              <CardTitle>Cupons resgatados</CardTitle>
              <CardDescription>Cupons em que este cliente entrou pelo código.</CardDescription>
            </CardHeader>
            <CardContent>
              {coupons.isLoading ? (
                <Skeleton className="h-20 w-full" />
              ) : (coupons.data ?? []).length === 0 ? (
                <p className="py-4 text-small text-muted-foreground">Nenhum cupom resgatado.</p>
              ) : (
                <ul className="space-y-2">
                  {couponsPage.pageItems.map((coupon) => (
                    <li key={coupon.id} className="flex flex-wrap items-center gap-2">
                      <Badge variant="outline" className="font-mono">
                        {coupon.code}
                      </Badge>
                      <span className="min-w-0 flex-1 truncate text-small">{coupon.description}</span>
                      {coupon.isActive ? (
                        <Badge variant="ok">Ativo</Badge>
                      ) : (
                        <Badge variant="muted">Inativo</Badge>
                      )}
                    </li>
                  ))}
                </ul>
              )}
              <Pagination {...couponsPage.paginationProps} itemLabel="cupons" className="-mx-4 mt-3 sm:-mx-5" />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="assinaturas">
          <Card>
            <CardHeader>
              <CardTitle>Assinaturas de pão</CardTitle>
              <CardDescription>
                O aplicativo cria e cancela a assinatura. Nem o backend nem o aplicativo do
                vendedor a processam hoje.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {subscriptions.isLoading ? (
                <Skeleton className="h-20 w-full" />
              ) : (subscriptions.data ?? []).length === 0 ? (
                <p className="py-4 text-small text-muted-foreground">Nenhuma assinatura.</p>
              ) : (
                <ul className="space-y-2">
                  {subscriptionsPage.pageItems.map((sub) => (
                    <li key={sub.id} className="flex flex-wrap items-center gap-2 text-small">
                      <Badge variant={sub.status === "active" ? "ok" : "muted"}>
                        {sub.status === "active"
                          ? "Ativa"
                          : sub.status === "cancelled"
                            ? "Cancelada"
                            : "Pendente"}
                      </Badge>
                      <span>
                        {formatNumber(sub.quantity)} unidades
                        {sub.deliverySchedule?.time ? ` às ${sub.deliverySchedule.time}` : ""}
                      </span>
                      <span className="text-muted-foreground">
                        {sub.deliverySchedule?.weekDays?.join(", ")}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              <Pagination {...subscriptionsPage.paginationProps} itemLabel="assinaturas" className="-mx-4 mt-3 sm:-mx-5" />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {editing ? <ClientEditDialog client={c} open={editing} onOpenChange={setEditing} /> : null}
    </>
  );
}
